/**
 * ScenarioPickerModal — choose which what-if scenario the analysis screens show,
 * or switch back to the original. Full scenario names get room here, so the
 * navbar toggle can stay compact.
 */
import { useSelector, useDispatch } from "react-redux";
import { activateScenario, deleteScenario } from "Redux/data/actions";

const BASE = "base";

/** Shared status colour for scenario dots (imported by ScenarioToggle). */
export const statusColor = (status) =>
  status === "ready" ? "#3DB887" :
  status === "running" ? "#E8A33D" :
  status === "failed" ? "#D64550" :
  status === "stale" ? "#9B8FB5" : "var(--ev-text-muted)";

const ScenarioPickerModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const scenarios = useSelector((s) => s.project.scenarios) || [];
  const active = useSelector((s) => s.data.activeScenario) || BASE;
  const running = useSelector(
    (s) => s.data.runStatus && !s.data.runStatus.done && !s.data.runStatus.error
  );

  const pick = (id) => {
    if (running) return;
    dispatch(activateScenario(id));
    onClose();
  };

  const remove = async (s, e) => {
    e.stopPropagation();
    if (running) return;
    if (window.confirm(`Delete scenario "${s.name || s.id}"? Its computed data will be removed.`)) {
      await dispatch(deleteScenario(s.id));
    }
  };

  const Row = ({ onClick, selected, children }) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        padding: "0.6rem 0.7rem",
        borderRadius: "8px",
        border: `1px solid ${selected ? "#E8A33D" : "var(--ev-border)"}`,
        backgroundColor: selected ? "rgba(232,163,61,0.10)" : "transparent",
        marginBottom: "0.4rem",
        cursor: running ? "not-allowed" : "pointer",
        opacity: running ? 0.6 : 1,
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="modal is-active">
      <div className="modal-background" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} onClick={onClose} />
      <div className="modal-card" style={{ maxWidth: "620px", borderRadius: "var(--ev-radius-lg)", overflow: "hidden" }}>
        <header style={{ background: "linear-gradient(135deg, #430098 0%, #79589F 100%)", padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <i className="fa-duotone fa-flask" style={{ color: "#fff" }} />
          <span style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", flex: 1 }}>Scenarios</span>
          <button className="delete" onClick={onClose} />
        </header>

        <section style={{ padding: "0.75rem", backgroundColor: "var(--ev-white)", maxHeight: "60vh", overflowY: "auto" }}>
          {running && (
            <div className="notification is-warning is-light" style={{ fontSize: "0.78rem" }}>
              A scenario is currently running — switching is disabled until it finishes.
            </div>
          )}

          <Row onClick={() => pick(BASE)} selected={active === BASE}>
            <i className="fa-duotone fa-file-invoice-dollar" style={{ color: "var(--ev-purple)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "0.84rem", color: "var(--ev-text)" }}>Original</div>
              <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)" }}>The real deal — unmodified input set.</div>
            </div>
            {active === BASE && <i className="fa-solid fa-check" style={{ color: "var(--ev-success)" }} />}
          </Row>

          {scenarios.map((s) => (
            <Row key={s.id} onClick={() => pick(s.id)} selected={active === s.id}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: statusColor(s.status), flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.84rem", color: "var(--ev-text)", wordBreak: "break-word" }}>
                  {s.name || s.id}
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)" }}>
                  {s.status || "ready"}
                  {s.preview?.removedRows !== undefined && (
                    <> · removes {s.preview.removedRows.toLocaleString()} rows
                      {s.preview.removedPct !== undefined && <> ({s.preview.removedPct}%)</>}
                    </>
                  )}
                </div>
              </div>
              {active === s.id && <i className="fa-solid fa-check" style={{ color: "var(--ev-success)", flexShrink: 0 }} />}
              <button
                className="button is-small is-light"
                title="Delete scenario"
                onClick={(e) => remove(s, e)}
                style={{ borderRadius: "6px", color: "var(--ev-danger)", flexShrink: 0 }}
              >
                <i className="fa-solid fa-trash" />
              </button>
            </Row>
          ))}
        </section>
      </div>
    </div>
  );
};

export default ScenarioPickerModal;
